import { useCallback, useEffect, useState, useMemo } from 'react'
import { salesApi, customersApi, sellerProductsApi, customerProductsApi, profileApi } from '../../services/api'
import { getCurrentPositionSafe } from '../../utils/geolocation'
import ExportBar, { ExportColumn } from '../../components/ExportBar'
import LocationPicker from '../../components/LocationPicker'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

const ROWS_PER_PAGE = 100

interface Customer {
  id: number
  name: string
  nickname: string | null
  district: string | null
  phone: string | null
  payment_mode: 'cash' | 'credit'
  channel: 'street' | 'wholesale'
  latitude: number | null
  longitude: number | null
}

interface SellerProduct {
  id: number
  product_id: number
  name: string
  presentation: string
  recommended_price: number
  category: string
  real_price: number
}

interface Sale {
  id: number
  seller_id: number
  seller_name: string
  customer_id: number | null
  customer_name: string | null
  customer_district: string | null
  customer_payment_mode: 'cash' | 'credit' | null
  variant_id: number | null
  seller_product_id: number | null
  variant_name: string
  sku: string | null
  category: string
  recommended_price: number | null
  real_price: number | null
  presentation: string
  quantity: number
  unit_price: number
  total_amount: number
  sale_date: string
  payment_date: string | null
  expected_payment_date: string | null
  partial_payments: number
  remanent_payment: number
  status: 'pending' | 'completed'
  notes: string | null
  gps_latitude: number | null
  gps_longitude: number | null
}

const SALES_EXPORT_COLUMNS: ExportColumn<Sale>[] = [
  { key: 'id', label: 'ID', format: (s) => `#${s.id}` },
  { key: 'sale_date', label: 'Fecha', format: (s) => new Date(s.sale_date).toLocaleDateString() },
  { key: 'customer_name', label: 'Cliente', format: (s) => s.customer_name || '-' },
  { key: 'customer_payment_mode', label: 'Modo', format: (s) => s.customer_payment_mode || '-' },
  { key: 'variant_name', label: 'Producto', format: (s) => s.variant_name },
  { key: 'presentation', label: 'Pres.', format: (s) => s.presentation },
  { key: 'quantity', label: 'Cant.', format: (s) => String(s.quantity) },
  { key: 'total_amount', label: 'Total', format: (s) => `$${Number(s.total_amount || 0).toFixed(2)}` },
  { key: 'partial_payments', label: 'Pagado', format: (s) => `$${Number(s.partial_payments || 0).toFixed(2)}` },
  { key: 'remanent_payment', label: 'Pendiente', format: (s) => `$${Number(s.remanent_payment || 0).toFixed(2)}` },
]

type Step = 'customer' | 'product' | 'review'

interface CartItem {
  seller_product_id: number
  product_name: string
  presentation: 'granel' | '250gr' | '1kg'
  quantity: number
  unit_price: number
  recommended_price: number
  sale_date: string
  payment_date: string
  expected_payment_date: string
  partial_payments: number
  notes: string
}

interface SaleForm {
  customer_id: number | null
  customer_search: string
  new_customer_name: string
  new_customer_phone: string
  new_customer_address: string
  new_customer_district: string
  new_customer_dni: string
  new_customer_ruc: string
  new_customer_payment_mode: 'cash' | 'credit'
  seller_product_id: number | null
  presentation: 'granel' | '250gr' | '1kg'
  quantity: number
  sale_date: string
  payment_date: string
  expected_payment_date: string
  partial_payments: number
  notes: string
  legacy_product_name: string | null
  legacy_unit_price: number | null
  gps_latitude: number | null
  gps_longitude: number | null
}

const emptyForm: SaleForm = {
  customer_id: null,
  customer_search: '',
  new_customer_name: '',
  new_customer_phone: '',
  new_customer_address: '',
  new_customer_district: '',
  new_customer_dni: '',
  new_customer_ruc: '',
  new_customer_payment_mode: 'cash',
  seller_product_id: null,
  presentation: 'granel',
  quantity: 1,
  sale_date: new Date().toISOString().split('T')[0],
  payment_date: '',
  expected_payment_date: '',
  partial_payments: 0,
  notes: '',
  legacy_product_name: null,
  legacy_unit_price: null,
  gps_latitude: null,
  gps_longitude: null,
}

export default function SalesPage() {
  const [sales, setSales] = useState<Sale[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [sellerProducts, setSellerProducts] = useState<SellerProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [step, setStep] = useState<Step>('customer')
  const [form, setForm] = useState<SaleForm>(emptyForm)
  const [cart, setCart] = useState<CartItem[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [showLocationPicker, setShowLocationPicker] = useState(false)
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [filterDistrict, setFilterDistrict] = useState('')
  const [filterPendingOnly, setFilterPendingOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [wholesalePrices, setWholesalePrices] = useState<Record<number, number>>({})
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentSale, setPaymentSale] = useState<Sale | null>(null)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0])
  const [paymentSaving, setPaymentSaving] = useState(false)
  const [canEdit, setCanEdit] = useState(false)

  const districts = useMemo(() => {
    const set = new Set<string>()
    sales.forEach((s) => {
      if (s.customer_district) set.add(s.customer_district)
    })
    return Array.from(set).sort()
  }, [sales])

  const fetchData = async () => {
    try {
      const [salesRes, customersRes, productsRes] = await Promise.all([
        salesApi.list(),
        customersApi.list(),
        sellerProductsApi.list(),
      ])
      setSales(salesRes.data.sales || [])
      setCustomers(customersRes.data.customers || [])
      setSellerProducts(productsRes.data.seller_products || [])
    } catch (err) {
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    profileApi.get().then((res) => setCanEdit(res.data.permissions?.edit_sales !== false))
  }, [])

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customer_id),
    [customers, form.customer_id]
  )
  const isWholesaleCustomer = selectedCustomer?.channel === 'wholesale'

  // A wholesale customer never uses the seller's street price -- the backend
  // rejects the sale outright if nothing is pactado for this account (see
  // _resolve_unit_price in api/app.py), so the form has to show the real
  // pactado price (or the fact that one is missing) instead of the street
  // price, or the seller would see one number here and a 400 at submit time.
  useEffect(() => {
    if (!isWholesaleCustomer || !form.customer_id) {
      setWholesalePrices({})
      return
    }
    customerProductsApi
      .list(form.customer_id)
      .then((res) => {
        const map: Record<number, number> = {}
        for (const cp of res.data.customer_products || []) {
          map[cp.product_id] = cp.real_price
        }
        setWholesalePrices(map)
      })
      .catch((err) => console.error('Error fetching negotiated prices:', err))
  }, [isWholesaleCustomer, form.customer_id])

  // Effective price for a seller_product given the currently selected
  // customer: the pactado price for a wholesale account (undefined if none
  // was pactado yet, meaning the sale will be rejected), or the seller's
  // own street price otherwise. Wrapped in useCallback (rather than a plain
  // function) so it has a stable identity across renders unless its real
  // inputs change, letting computedTotal's useMemo list it as a dependency
  // instead of triggering an exhaustive-deps warning (CI treats ESLint
  // warnings as failures here, max: 0).
  const effectivePrice = useCallback(
    (p: SellerProduct): number | undefined => (isWholesaleCustomer ? wholesalePrices[p.product_id] : p.real_price),
    [isWholesaleCustomer, wholesalePrices]
  )

  const filteredCustomers = useMemo(() => {
    if (!form.customer_search) return customers
    const q = form.customer_search.toLowerCase()
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.nickname && c.nickname.toLowerCase().includes(q)) ||
        (c.district && c.district.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q))
    )
  }, [customers, form.customer_search])

  const selectedProduct = useMemo(
    () => sellerProducts.find((p) => p.id === form.seller_product_id),
    [sellerProducts, form.seller_product_id]
  )

  const isLegacyProduct = editingId !== null && !form.seller_product_id && !!form.legacy_product_name

  const computedTotal = useMemo(() => {
    const unitPrice = selectedProduct ? (effectivePrice(selectedProduct) ?? 0) : (form.legacy_unit_price || 0)
    return unitPrice * form.quantity
  }, [selectedProduct, form.legacy_unit_price, form.quantity, effectivePrice])

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.unit_price * item.quantity, 0),
    [cart]
  )
  const cartPaid = useMemo(
    () => cart.reduce((sum, item) => sum + item.partial_payments, 0),
    [cart]
  )

  const filtered = useMemo(() => {
    return sales.filter((s) => {
      if (!s.sale_date) return false
      if (filterDateFrom) {
        const saleDate = new Date(s.sale_date)
        const from = new Date(filterDateFrom)
        if (saleDate < from) return false
      }
      if (filterDateTo) {
        const saleDate = new Date(s.sale_date)
        const to = new Date(filterDateTo + 'T23:59:59')
        if (saleDate > to) return false
      }
      if (filterCustomer) {
        const q = filterCustomer.toLowerCase()
        if (!(s.customer_name || '').toLowerCase().includes(q)) return false
      }
      if (filterDistrict) {
        const q = filterDistrict.toLowerCase()
        if (!(s.customer_district || '').toLowerCase().includes(q)) return false
      }
      if (filterPendingOnly && !(s.remanent_payment > 0)) return false
      return true
    })
  }, [sales, filterDateFrom, filterDateTo, filterCustomer, filterDistrict, filterPendingOnly])

  const { sortedData: sortedFiltered, sortConfig, requestSort } = useSortableData(filtered)

  const totalPages = Math.ceil(sortedFiltered.length / ROWS_PER_PAGE)
  const paged = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE
    return sortedFiltered.slice(start, start + ROWS_PER_PAGE)
  }, [sortedFiltered, page])

  const clearFilters = () => {
    setFilterDateFrom('')
    setFilterDateTo('')
    setFilterCustomer('')
    setFilterDistrict('')
    setFilterPendingOnly(false)
    setPage(1)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: name === 'quantity' || name === 'partial_payments' ? Number(value) : value,
      ...(name === 'customer_search' ? { customer_id: null } : {}),
    }))
  }

  const selectCustomer = (c: Customer) => {
    setForm((prev) => ({
      ...prev,
      customer_id: c.id,
      customer_search: `${c.name}${c.nickname ? ` (${c.nickname})` : ''}`,
      new_customer_payment_mode: c.payment_mode,
    }))
    setStep('product')
  }

  const selectNewCustomer = () => {
    setStep('product')
  }

  const openNew = () => {
    setForm(emptyForm)
    setCart([])
    setEditingId(null)
    setShowLocationPicker(false)
    setStep('customer')
    setError('')
    setShowForm(true)
  }

  const openEdit = (sale: Sale) => {
    const saleDate = sale.sale_date ? sale.sale_date.split('T')[0] : ''
    const payDate = sale.payment_date ? sale.payment_date.split('T')[0] : ''
    const expDate = sale.expected_payment_date ? sale.expected_payment_date.split('T')[0] : ''
    // When the sale is tied to a catalog product, the product's own presentation
    // is the source of truth (the backend overrides whatever is sent otherwise).
    const linkedProduct = sale.seller_product_id
      ? sellerProducts.find((p) => p.id === sale.seller_product_id)
      : undefined

    // Default the sale's own location to whatever it already has; if it
    // never got one (e.g. created before GPS capture existed, or the
    // silent capture failed), fall back to the customer's registered
    // location -- that's the best guess for where this sale happened.
    const customer = customers.find((c) => c.id === sale.customer_id)
    const defaultLat = sale.gps_latitude ?? customer?.latitude ?? null
    const defaultLng = sale.gps_longitude ?? customer?.longitude ?? null

    setForm({
      customer_id: sale.customer_id,
      customer_search: sale.customer_name || '',
      new_customer_name: '',
      new_customer_phone: '',
      new_customer_address: '',
      new_customer_district: '',
      new_customer_dni: '',
      new_customer_ruc: '',
      new_customer_payment_mode: 'cash',
      seller_product_id: sale.seller_product_id,
      presentation: (linkedProduct ? linkedProduct.presentation : sale.presentation) as 'granel' | '250gr' | '1kg',
      quantity: sale.quantity,
      sale_date: saleDate,
      payment_date: payDate,
      expected_payment_date: expDate,
      partial_payments: sale.partial_payments || 0,
      notes: sale.notes || '',
      legacy_product_name: sale.seller_product_id ? null : sale.variant_name,
      legacy_unit_price: sale.seller_product_id ? null : sale.unit_price,
      gps_latitude: defaultLat,
      gps_longitude: defaultLng,
    })
    setCart([])
    setEditingId(sale.id)
    setShowLocationPicker(defaultLat != null && defaultLng != null)
    setStep('product')
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async () => {
    setSaving(true)
    setError('')
    try {
      let customerId = form.customer_id

      // Create new customer if needed
      if (!customerId && form.new_customer_name.trim()) {
        const custRes = await customersApi.create({
          name: form.new_customer_name,
          phone: form.new_customer_phone || null,
          address: form.new_customer_address || null,
          district: form.new_customer_district || null,
          dni: form.new_customer_dni || null,
          ruc: form.new_customer_ruc || null,
          payment_mode: form.new_customer_payment_mode,
        })
        customerId = custRes.data.id
      }

      if (!customerId) {
        setError('Por favor selecciona o crea un cliente')
        setSaving(false)
        return
      }

      if (!form.seller_product_id && !isLegacyProduct) {
        setError('Por favor selecciona un producto')
        setSaving(false)
        return
      }

      if (selectedProduct && isWholesaleCustomer && effectivePrice(selectedProduct) === undefined) {
        setError('Esta cuenta no tiene precio pactado para este producto todavía')
        setSaving(false)
        return
      }

      const payload = {
        customer_id: customerId,
        seller_product_id: form.seller_product_id,
        presentation: form.presentation,
        quantity: form.quantity,
        sale_date: form.sale_date || undefined,
        payment_date: form.payment_date || null,
        expected_payment_date: form.expected_payment_date || null,
        partial_payments: form.partial_payments || 0,
        notes: form.notes || null,
        gps_latitude: form.gps_latitude,
        gps_longitude: form.gps_longitude,
      }

      await salesApi.update(editingId as number, payload)

      setShowForm(false)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar venta')
    } finally {
      setSaving(false)
    }
  }

  const handleAddToCart = () => {
    if (!form.seller_product_id) {
      setError('Por favor selecciona un producto')
      return
    }
    const product = sellerProducts.find((p) => p.id === form.seller_product_id)
    if (!product) return
    const unitPrice = effectivePrice(product)
    if (unitPrice === undefined) {
      setError('Esta cuenta no tiene precio pactado para este producto todavía')
      return
    }

    setCart((prev) => [
      ...prev,
      {
        seller_product_id: product.id,
        product_name: product.name,
        presentation: product.presentation as 'granel' | '250gr' | '1kg',
        quantity: form.quantity,
        unit_price: unitPrice,
        recommended_price: product.recommended_price,
        sale_date: form.sale_date,
        payment_date: form.payment_date,
        expected_payment_date: form.expected_payment_date,
        partial_payments: form.partial_payments,
        notes: form.notes,
      },
    ])
    setError('')
    setForm((prev) => ({
      ...prev,
      seller_product_id: null,
      presentation: 'granel',
      quantity: 1,
      payment_date: '',
      expected_payment_date: '',
      partial_payments: 0,
      notes: '',
    }))
  }

  const handleRemoveFromCart = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }

  const handleConfirmSale = async () => {
    setSaving(true)
    setError('')
    try {
      let customerId = form.customer_id

      if (!customerId && form.new_customer_name.trim()) {
        const custRes = await customersApi.create({
          name: form.new_customer_name,
          phone: form.new_customer_phone || null,
          address: form.new_customer_address || null,
          district: form.new_customer_district || null,
          dni: form.new_customer_dni || null,
          ruc: form.new_customer_ruc || null,
          payment_mode: form.new_customer_payment_mode,
        })
        customerId = custRes.data.id
      }

      if (!customerId) {
        setError('Por favor selecciona o crea un cliente')
        setSaving(false)
        return
      }

      if (cart.length === 0) {
        setError('Agrega al menos un producto')
        setSaving(false)
        return
      }

      // Silent, non-blocking GPS capture: if permission is denied or the
      // request times out, this resolves to null and the sale is still
      // registered without coordinates.
      const pos = await getCurrentPositionSafe()

      await salesApi.createBulk({
        customer_id: customerId,
        gps_latitude: pos?.latitude ?? null,
        gps_longitude: pos?.longitude ?? null,
        items: cart.map((item) => ({
          seller_product_id: item.seller_product_id,
          presentation: item.presentation,
          quantity: item.quantity,
          sale_date: item.sale_date || undefined,
          payment_date: item.payment_date || null,
          expected_payment_date: item.expected_payment_date || null,
          partial_payments: item.partial_payments || 0,
          notes: item.notes || null,
        })),
      })

      setCart([])
      setShowForm(false)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrar la venta')
    } finally {
      setSaving(false)
    }
  }

  const openPaymentModal = (sale: Sale) => {
    setPaymentSale(sale)
    setPaymentAmount('')
    setPaymentDate(new Date().toISOString().split('T')[0])
    setShowPaymentModal(true)
  }

  const handlePaymentSubmit = async () => {
    if (!paymentSale || !paymentAmount) return
    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) return

    setPaymentSaving(true)
    try {
      const newPartial = Number(paymentSale.partial_payments || 0) + amount
      await salesApi.update(paymentSale.id, {
        partial_payments: newPartial,
        payment_date: paymentDate || undefined,
      })
      setShowPaymentModal(false)
      fetchData()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al registrar pago')
    } finally {
      setPaymentSaving(false)
    }
  }

  const handleDelete = async (sale: Sale) => {
    if (!confirm(`¿Eliminar la venta #${sale.id}? Esta acción no se puede deshacer.`)) return
    try {
      await salesApi.delete(sale.id)
      fetchData()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al eliminar la venta')
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Ventas</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nueva Venta
        </button>
      </div>

      {/* Sale Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Venta' : 'Registrar Venta'}
              </h2>
              {/* Step Indicator */}
              {!editingId && (
                <div className="flex mt-3 space-x-2">
                  {(['customer', 'product', 'review'] as Step[]).map((s, i) => (
                    <div
                      key={s}
                      className={`flex items-center text-xs ${
                        step === s ? 'text-terracotta-700 font-semibold' : 'text-espresso-500'
                      }`}
                    >
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center mr-1 ${
                          step === s ? 'bg-terracotta-600 text-white' : 'bg-cream-200 text-espresso-600'
                        }`}
                      >
                        {i + 1}
                      </span>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                      {i < 2 && <span className="mx-1">→</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm mb-4">{error}</div>
              )}

              {/* Step: Customer */}
              {step === 'customer' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-espresso-700">Selecciona o crea un cliente</h3>

                  {/* Search existing */}
                  <div>
                    <input
                      type="text"
                      placeholder="Buscar por nombre, apodo, distrito o teléfono..."
                      value={form.customer_search}
                      onChange={handleChange}
                      name="customer_search"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>

                  {form.customer_id ? (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                      Cliente seleccionado: <strong>{form.customer_search}</strong>
                      <button
                        onClick={() => {
                          if (cart.length > 0 && !confirm('Cambiar de cliente vaciará los productos ya agregados al carrito. ¿Continuar?')) {
                            return
                          }
                          setCart([])
                          setForm((p) => ({ ...p, customer_id: null, customer_search: '' }))
                        }}
                        className="ml-2 text-green-600 underline"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-espresso-200 rounded-md divide-y divide-espresso-100">
                      {filteredCustomers.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-espresso-500">No se encontraron clientes</div>
                      ) : (
                        filteredCustomers.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => selectCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-terracotta-50 text-sm flex justify-between"
                          >
                            <span>
                              {c.name}
                              {c.nickname && <span className="text-espresso-500 ml-1">({c.nickname})</span>}
                            </span>
                            <span className="text-espresso-500">
                              {c.district} · {c.phone || '-'}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {/* New customer inline */}
                  <div className="border-t border-espresso-200 pt-4">
                    <p className="text-xs font-medium text-espresso-500 uppercase mb-3">O registrar nuevo cliente</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        name="new_customer_name"
                        value={form.new_customer_name}
                        onChange={handleChange}
                        placeholder="Nombre *"
                        className="col-span-2 border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <input
                        type="text"
                        name="new_customer_phone"
                        value={form.new_customer_phone}
                        onChange={handleChange}
                        placeholder="Teléfono"
                        className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <input
                        type="text"
                        name="new_customer_district"
                        value={form.new_customer_district}
                        onChange={handleChange}
                        placeholder="Distrito"
                        className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <input
                        type="text"
                        name="new_customer_address"
                        value={form.new_customer_address}
                        onChange={handleChange}
                        placeholder="Dirección"
                        className="col-span-2 border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <div>
                        <label className="block text-xs text-espresso-500 mb-1">Modo de Pago</label>
                        <select
                          name="new_customer_payment_mode"
                          value={form.new_customer_payment_mode}
                          onChange={handleChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="cash">Efectivo</option>
                          <option value="credit">Crédito</option>
                        </select>
                      </div>
                    </div>
                    {form.new_customer_name.trim() && (
                      <button
                        onClick={selectNewCustomer}
                        className="mt-3 px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm hover:bg-terracotta-700"
                      >
                        Continuar con nuevo cliente →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Step: Product */}
              {step === 'product' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-espresso-700">Seleccionar producto</h3>
                    {!editingId && (
                      <button onClick={() => setStep('customer')} className="text-xs text-terracotta-700 underline">
                        ← Volver
                      </button>
                    )}
                  </div>

                  {editingId && (
                    <>
                      <div className="bg-cream-50 border border-espresso-200 rounded-md p-3 text-sm text-espresso-700">
                        Cliente: <strong>{form.customer_search}</strong>
                      </div>

                      <div>
                        {showLocationPicker ? (
                          <>
                            <label className="block text-sm font-medium text-espresso-700 mb-1">
                              Ubicación de esta venta
                            </label>
                            <p className="text-xs text-espresso-500 mb-2">
                              Por defecto, la ubicación registrada del cliente. Puedes ajustarla si esta venta
                              ocurrió en otro punto.
                            </p>
                            <LocationPicker
                              latitude={form.gps_latitude}
                              longitude={form.gps_longitude}
                              onChange={(lat, lng) =>
                                setForm((p) => ({ ...p, gps_latitude: lat, gps_longitude: lng }))
                              }
                            />
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowLocationPicker(true)}
                            className="text-sm text-terracotta-700 hover:text-terracotta-800 underline"
                          >
                            + Agregar ubicación en el mapa (opcional)
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {isLegacyProduct && (
                    <div className="bg-cream-50 border border-espresso-200 rounded-md p-3 text-sm text-espresso-700">
                      Esta venta usa un producto del catálogo antiguo (<strong>{form.legacy_product_name}</strong>) y
                      no se puede cambiar desde aquí. Puedes editar cantidad y datos de pago, o elegir un producto de
                      tu catálogo abajo para reemplazarlo.
                    </div>
                  )}

                  {sellerProducts.length === 0 ? (
                    <p className="text-sm text-espresso-500">
                      No tienes productos en tu catálogo todavía. Ve a "Productos" para agregar los que vas a vender.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                      {sellerProducts.map((p) => (
                        <button
                          key={p.id}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              seller_product_id: p.id,
                              presentation: p.presentation as 'granel' | '250gr' | '1kg',
                            }))
                          }
                          className={`text-left p-3 rounded-md border text-sm ${
                            form.seller_product_id === p.id
                              ? 'border-terracotta-500 bg-terracotta-50 ring-1 ring-terracotta-500'
                              : 'border-espresso-200 hover:border-espresso-300'
                          }`}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{p.name}</span>
                            {effectivePrice(p) !== undefined ? (
                              <span className="text-espresso-600">${Number(effectivePrice(p)).toFixed(2)}</span>
                            ) : (
                              <span className="text-red-600 text-xs font-medium">Sin precio pactado</span>
                            )}
                          </div>
                          <div className="text-xs text-espresso-500 mt-1">
                            Presentación: {p.presentation}
                            {isWholesaleCustomer
                              ? effectivePrice(p) !== undefined && ' · Precio pactado para esta cuenta'
                              : ` · Precio sugerido: $${Number(p.recommended_price).toFixed(2)}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {(form.seller_product_id || isLegacyProduct) && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-espresso-700 mb-1">Presentación</label>
                          {isLegacyProduct ? (
                            <select
                              name="presentation"
                              value={form.presentation}
                              onChange={handleChange}
                              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                            >
                              <option value="granel">Granel</option>
                              <option value="250gr">250gr</option>
                              <option value="1kg">1kg</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              disabled
                              readOnly
                              value={selectedProduct ? selectedProduct.presentation : form.presentation}
                              title="La presentación viene definida por el producto seleccionado"
                              className="w-full border border-espresso-200 rounded-md px-3 py-2 text-sm bg-cream-100 text-espresso-500 cursor-not-allowed"
                            />
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-espresso-700 mb-1">Cantidad (1-500)</label>
                          <input
                            type="number"
                            name="quantity"
                            value={form.quantity}
                            onChange={handleChange}
                            min={1}
                            max={500}
                            className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                          />
                        </div>
                      </div>

                      <div className="bg-cream-50 rounded-md p-3 text-sm">
                        {selectedProduct && (
                          <div className="flex justify-between text-espresso-600">
                            <span>Precio Sugerido:</span>
                            <span>${Number(selectedProduct.recommended_price).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span>Precio de Venta:</span>
                          <span>
                            ${Number(
                              selectedProduct ? (effectivePrice(selectedProduct) ?? 0) : form.legacy_unit_price || 0
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between font-semibold mt-1">
                          <span>Total:</span>
                          <span>${computedTotal.toFixed(2)}</span>
                        </div>
                        {isWholesaleCustomer && selectedProduct && effectivePrice(selectedProduct) === undefined && (
                          <p className="mt-2 text-xs text-red-600">
                            Esta cuenta no tiene precio pactado para este producto todavía — ve a la pestaña
                            Mayoristas y pacta un precio antes de registrar la venta.
                          </p>
                        )}
                      </div>

                      {!editingId && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de Venta</label>
                              <input
                                type="date"
                                name="sale_date"
                                value={form.sale_date}
                                onChange={handleChange}
                                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-espresso-700 mb-1">
                                Fecha de Pago Esperada
                              </label>
                              <input
                                type="date"
                                name="expected_payment_date"
                                value={form.expected_payment_date}
                                onChange={handleChange}
                                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-espresso-700 mb-1">
                                Pagos Parciales ($)
                              </label>
                              <input
                                type="number"
                                name="partial_payments"
                                value={form.partial_payments}
                                onChange={handleChange}
                                min={0}
                                step={0.01}
                                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de Pago</label>
                              <input
                                type="date"
                                name="payment_date"
                                value={form.payment_date}
                                onChange={handleChange}
                                className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-espresso-700 mb-1">
                          Detalles
                        </label>
                        <textarea
                          name="notes"
                          value={form.notes}
                          onChange={handleChange}
                          rows={2}
                          placeholder="Detalles opcionales..."
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>

                      {!editingId && (
                        <button
                          type="button"
                          onClick={handleAddToCart}
                          className="w-full px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                        >
                          + Agregar Producto
                        </button>
                      )}
                    </>
                  )}

                  {!editingId && cart.length > 0 && (
                    <div className="border-t border-espresso-200 pt-4 space-y-2">
                      <h4 className="text-sm font-medium text-espresso-700">
                        Productos agregados ({cart.length})
                      </h4>
                      {cart.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-cream-50 rounded-md p-3 text-sm"
                        >
                          <div>
                            <div className="font-medium text-espresso-800">
                              {item.product_name} × {item.quantity} ({item.presentation})
                            </div>
                            <div className="text-xs text-espresso-500">
                              Total: ${(item.unit_price * item.quantity).toFixed(2)}
                              {item.partial_payments > 0 && ` · Pagado: $${item.partial_payments.toFixed(2)}`}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromCart(index)}
                            className="text-red-600 hover:text-red-900 text-xs ml-3"
                          >
                            Quitar
                          </button>
                        </div>
                      ))}
                      <div className="flex justify-between font-semibold text-sm pt-1">
                        <span>Total del carrito:</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                      <button
                        onClick={() => setStep('review')}
                        className="w-full px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                      >
                        Continuar a Revisar →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step: Review (new sales only) */}
              {step === 'review' && !editingId && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-espresso-700">Revisar y Confirmar</h3>
                    <button onClick={() => setStep('product')} className="text-xs text-terracotta-700 underline">
                      ← Volver
                    </button>
                  </div>

                  <div className="bg-cream-50 rounded-md p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-espresso-500">Cliente:</span>
                      <span className="font-medium">{form.customer_search || form.new_customer_name}</span>
                    </div>
                    <div className="border-t border-espresso-200 pt-2 space-y-2">
                      {cart.map((item, index) => (
                        <div key={index} className="flex justify-between">
                          <span>
                            {item.product_name} × {item.quantity} ({item.presentation})
                          </span>
                          <span className="text-right">
                            ${(item.unit_price * item.quantity).toFixed(2)}
                            {item.partial_payments > 0 && (
                              <span className="block text-xs text-green-600">
                                Pagado: ${item.partial_payments.toFixed(2)}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between border-t border-espresso-200 pt-2 font-semibold">
                      <span>Total:</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                    {cartPaid > 0 && (
                      <>
                        <div className="flex justify-between text-green-600">
                          <span>Pagado:</span>
                          <span>${cartPaid.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold text-red-600">
                          <span>Pendiente:</span>
                          <span>${(cartTotal - cartPaid).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    onClick={handleConfirmSale}
                    disabled={saving}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Guardando...' : `Confirmar ${cart.length > 1 ? 'Ventas' : 'Venta'}`}
                  </button>
                </div>
              )}

              {/* Edit mode: single submit */}
              {editingId && (
                <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                  >
                    {saving ? 'Guardando...' : 'Actualizar Venta'}
                  </button>
                </div>
              )}

              {/* Close button for non-review steps */}
              {step !== 'review' && !editingId && (
                <div className="flex justify-end pt-4 border-t border-espresso-200 mt-4">
                  <button
                    onClick={() => {
                      setCart([])
                      setShowForm(false)
                    }}
                    className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-50"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sales Table */}
      {sales.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay ventas aún. Registra tu primera venta para comenzar.</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="bg-cream-50 shadow rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-espresso-700">Filtros</h3>
              <button onClick={clearFilters} className="text-xs text-terracotta-700 hover:text-terracotta-800">Limpiar todo</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-espresso-500 mb-1">Desde</label>
                <input type="date" value={filterDateFrom}
                  onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1) }}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-espresso-500 mb-1">Hasta</label>
                <input type="date" value={filterDateTo}
                  onChange={(e) => { setFilterDateTo(e.target.value); setPage(1) }}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-espresso-500 mb-1">Cliente</label>
                <input type="text" value={filterCustomer}
                  placeholder="Buscar..."
                  onChange={(e) => { setFilterCustomer(e.target.value); setPage(1) }}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-espresso-500 mb-1">Distrito</label>
                <select value={filterDistrict} onChange={(e) => { setFilterDistrict(e.target.value); setPage(1) }}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                  <option value="">Todos</option>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input type="checkbox" id="filterPendingOnly" checked={filterPendingOnly}
                onChange={(e) => { setFilterPendingOnly(e.target.checked); setPage(1) }}
                className="h-4 w-4 rounded border-espresso-300 text-terracotta-600 focus:ring-2 focus:ring-terracotta-500" />
              <label htmlFor="filterPendingOnly" className="ml-2 text-xs font-medium text-espresso-500">
                Solo con cobranza pendiente
              </label>
            </div>
          </div>

          {/* Results info + top pagination */}
          <div className="flex items-center justify-between mb-3 text-sm text-espresso-600">
            <span>{filtered.length} venta{filtered.length !== 1 ? 's' : ''} encontrada{filtered.length !== 1 ? 's' : ''}</span>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                  className="px-3 py-1 border border-espresso-300 rounded-md text-sm hover:bg-cream-50 disabled:opacity-50">Anterior</button>
                <span>Página {page} de {totalPages}</span>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                  className="px-3 py-1 border border-espresso-300 rounded-md text-sm hover:bg-cream-50 disabled:opacity-50">Siguiente</button>
              </div>
            )}
          </div>

          <ExportBar data={filtered} columns={SALES_EXPORT_COLUMNS} filename="ventas" title="Ventas" />

          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-espresso-100">
                <thead className="bg-cream-50">
                  <tr>
                    <SortableTh label="ID" sortKey="id" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Fecha" sortKey="sale_date" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Cliente" sortKey="customer_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Modo" sortKey="customer_payment_mode" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Producto" sortKey="variant_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Pres." sortKey="presentation" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Cant." sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Total" sortKey="total_amount" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Pagado" sortKey="partial_payments" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Pendiente" sortKey="remanent_payment" sortConfig={sortConfig} onSort={requestSort} />
                    <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-cream-50 divide-y divide-espresso-100">
                  {paged.map((sale) => (
                    <tr key={sale.id} className="hover:bg-cream-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800 font-medium">
                        #{sale.id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                        {new Date(sale.sale_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="font-medium text-espresso-800">{sale.customer_name || '-'}</div>
                        {sale.customer_district && (
                          <div className="text-xs text-espresso-500">{sale.customer_district}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {sale.customer_payment_mode ? (
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            sale.customer_payment_mode === 'credit'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {sale.customer_payment_mode}
                          </span>
                        ) : (
                          <span className="text-espresso-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                        <div>{sale.variant_name}</div>
                        {sale.recommended_price !== null && (
                          <div className="text-xs text-espresso-500">
                            Venta: ${Number(sale.unit_price).toFixed(2)} · Sugerido: ${Number(sale.recommended_price).toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">{sale.presentation}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">{sale.quantity}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-espresso-800">
                        ${Number(sale.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600">
                        ${Number(sale.partial_payments || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={sale.remanent_payment > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                          ${Number(sale.remanent_payment || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        {canEdit ? (
                          <div className="flex items-center justify-end gap-1">
                            {sale.remanent_payment > 0 && (
                              <TableActionButton onClick={() => openPaymentModal(sale)} variant="success">Pagar</TableActionButton>
                            )}
                            <TableActionButton onClick={() => openEdit(sale)} variant="primary">Editar</TableActionButton>
                            <TableActionButton onClick={() => handleDelete(sale)} variant="danger">Eliminar</TableActionButton>
                          </div>
                        ) : (
                          <span className="text-espresso-400 text-xs">Sin permiso</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {paged.length === 0 && (
              <div className="text-center py-8 text-espresso-500">No hay ventas que coincidan con los filtros.</div>
            )}
          </div>

          {/* Bottom pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4 space-x-2 text-sm">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">Primero</button>
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">Anterior</button>
              <span className="text-espresso-600">Página {page} de {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">Siguiente</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-3 py-1 border border-espresso-300 rounded-md hover:bg-cream-50 disabled:opacity-50">Último</button>
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Registrar Pago - Venta #{paymentSale.id}</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-cream-50 rounded-md p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-espresso-500">Total:</span>
                  <span className="font-medium">${Number(paymentSale.total_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Pagado:</span>
                  <span>${Number(paymentSale.partial_payments || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-espresso-200 pt-1">
                  <span>Pendiente:</span>
                  <span className="text-red-600">${Number(paymentSale.remanent_payment || 0).toFixed(2)}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Monto a pagar ($)</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min={0.01}
                  max={paymentSale.remanent_payment}
                  step={0.01}
                  placeholder="0.00"
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de pago</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={paymentSaving || !paymentAmount}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {paymentSaving ? 'Guardando...' : 'Registrar Pago'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
